import { useState, FunctionComponent } from 'react';
import { Button, IconButton } from '../../tailwindcomponents';
import { Edit, Check, Close, Delete, AddCircle, CheckCircle, RemoveCircle, Info, Announcement } from '../../components/icons';
import { DatePicker } from './../../MaterialUiComponents';
import { Autocomplete, TextField, Typography, Input, Snackbar, Dialog, Table, TableCell, TableRow } from './../../tailwindcomponents';
import { CertificateData, CertificateRights, CertificateRating, CertificateAircraftCategory, RatingsDifference, License } from './../../types/certificates';
import { RootState } from '../../reducer';
import { useSelector } from 'react-redux';
import CertificatesApi from '../../api/certificates';
import ProcessApproval from '../Certificates/ProcessApproval';
import CertificateInfo from '../Certificates/CertificateInfo';
import { DateTime } from 'luxon';
import { TableColumns } from '../../types/table';
import { palette } from '../../theme/palette';

type AlertColor = 'success' | 'info' | 'warning' | 'error';

type CertificateProps = {
    selectedCertificate: CertificateData,
    setSelectedCertificate: Function,
    closeCertificate: Function,
    viewType: 'view' | 'edit' | 'approval' | 'history',
    setLoading: Function,
    userType: string,
    shouldBeAbleToClose?: boolean | undefined,
    ratingsDifference?: RatingsDifference,
    ratings?: CertificateRating[],
    categories?: CertificateAircraftCategory[],
    catByRatingString?: Record<string, string[]>,
    licenses?: License[],
}

// Every column is non-sortable while a row is open for edit. `editRow` is a positional
// index (compared `editRow === index`), and sorting reorders the rows inside Table without
// telling this page, so a header click used to move the edit controls onto a different
// certificate - with the risk of saving over the wrong record. Sort first, then edit.
const sortTableColumnsInEdit = [
    { label: "Ratings", key: "rating_name", sortable: false },
    { label: "Aircraft / Category", key: "aircraft_category_name", sortable: false },
    { label: "Mxi Code", key: "mxi_code", sortable: false },
    { label: "Effective date", key: "effective_date", sortable: false },
    { label: "Expiry interval", key: "expiry_interval", sortable: false },
    { label: "Status", key: "expired_date_status", sortable: false },
    // Trailing actions cell rendered by generateTableBody - needs a header so the
    // header and body column counts match (skipIndexCol leaves no filler).
    { label: "", key: "", sortable: false },
] as TableColumns[];

const sortTableColumns = [
    { label: "Ratings", key: "rating_name" },
    { label: "Aircraft / Category", key: "aircraft_category_name" },
    { label: "Status", key: "expired_date_status" },
    { label: "", key: "", sortable: false },
] as TableColumns[];

const Certificate: FunctionComponent<CertificateProps> = ({
    selectedCertificate,
    setSelectedCertificate,
    closeCertificate,
    viewType,
    setLoading,
    shouldBeAbleToClose,
    ratingsDifference,
    ratings,
    categories,
    catByRatingString,
    licenses
}) => {
    const [editRow, setEditRow] = useState<number>(-1);
    const [tempRating, setTempRating] = useState<string>("");
    const [tempAircraftCategory, setTempAircraftCategory] = useState<string>("");
    const [emptyValueValidation, setEmptyValueValidation] = useState<string>("");
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedEffectiveDate, setSelectedEffectiveDate] = useState<Date | null>(null);
    const [changesMade, setChangesMade] = useState<string[]>([]);
    const [confirmDialog, setConfirmDialog] = useState<boolean>(false);
    const [jiraLink, setJiraLink] = useState<string>("");
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

    const store = useSelector((state: RootState) => state);
    const userId = store.user.me.user_id;

    const allowedCategories: string[] =
        tempRating
            ? catByRatingString?.[tempRating] ?? []
            : [];

    const findExpInterval = (
        selectedRatingName: string,
        selectedCategoryName: string
    ): number | null => {
        if (!ratings || !categories || !licenses) return null;

        const rating = ratings.find(r => r.rating === selectedRatingName);
        const category = categories.find(c => c.aircraft_category === selectedCategoryName);
        if (!rating?.id || !category?.id) return null;

        const lic = licenses.find(
            l => l.rating_id === rating.id && l.category_id === category.id
        );

        return lic?.interval_months ?? null;
    };

    const findLicenseCode = (opts: { ratingName: string; categoryName: string }): string => {
        const rating = ratings?.find(r => r.rating === opts.ratingName);
        const category = categories?.find(c => c.aircraft_category === opts.categoryName);
        if (!rating?.id || !category?.id) return "";
        const lic = licenses?.find(c => c.rating_id === rating.id && c.category_id === category.id);
        return lic?.license_code ?? "";
    };

    // 🔐 Helper: find the real index of a row in certificate_rights, even if table is sorted
    const findRowIndex = (row: CertificateRights): number => {
        // If the row has an id, prefer matching by id
        if (row.id != null) {
            const idxById = selectedCertificate.certificate_rights.findIndex(
                r => r.id === row.id
            );
            if (idxById !== -1) {
                return idxById;
            }
        }

        return selectedCertificate.certificate_rights.indexOf(row);
    };

    const generateTableBody = (row: CertificateRights, index: number) => {
        const isEditingRow = editRow === index;

        // When editing, use temp values if set, otherwise the row's original values
        const currentRating = isEditingRow && tempRating ? tempRating : row.rating_name;
        const currentCategory = isEditingRow && tempAircraftCategory
            ? tempAircraftCategory
            : row.aircraft_category_name;

        const licenseCode = findLicenseCode({
            ratingName: currentRating,
            categoryName: currentCategory
        });

        const interval = findExpInterval(currentRating, currentCategory);

        return (
            <TableRow className={isEditingRow ? 'bg-[color:var(--ds-accent-soft)]' : ''}>
                {/* Rating */}
                <TableCell>
                    {isEditingRow ? (
                        <Autocomplete
                            fullWidth
                            className="flex justify-center w-full"
                            options={ratings?.map(r => r.rating) ?? []}
                            value={tempRating ?? ''}
                            noOptionsText="No Ratings Found"
                            clearOnEscape
                            autoHighlight
                            onChange={(_, newVal) => {
                                const safeVal = newVal ?? '';
                                setTempRating(safeVal);
                                setTempAircraftCategory('');
                                setTempValueForRow(safeVal, 'ratings');
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Ratings"
                                    margin="none"
                                    variant="outlined"
                                    size="medium"
                                    fullWidth
                                />
                            )}
                        />
                    ) : (
                        row.rating_name
                    )}
                </TableCell>

                {/* Aircraft / Category */}
                <TableCell>
                    {isEditingRow ? (
                        <Autocomplete
                            fullWidth
                            className="flex justify-center w-full"
                            options={allowedCategories}
                            value={tempAircraftCategory}
                            noOptionsText="No categories found"
                            clearOnEscape
                            autoHighlight
                            onChange={(_, newValue: string | null) => {
                                if (newValue !== null) setTempValueForRow(newValue, 'aircraft');
                            }}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Aircraft Categories"
                                    margin="none"
                                    variant="outlined"
                                    size="medium"
                                    fullWidth
                                />
                            )}
                        />
                    ) : (
                        row.aircraft_category_name
                    )}
                </TableCell>

                {/* Mxi Code */}
                {editRow !== -1 && (
                    <TableCell>
                        {isEditingRow
                            ? findLicenseCode({
                                ratingName: currentRating,
                                categoryName: currentCategory
                            })
                            : licenseCode}
                    </TableCell>
                )}

                {/* Effective date */}
                {editRow !== -1 && (
                    <TableCell>
                        {isEditingRow ? (
                            <DatePicker
                                disableFuture={true}
                                onDateChange={(date: Date) => onDateChange(date)}
                                type={'Date'}
                                label="Choose effective date"
                                selected={selectedEffectiveDate ?? (row.effective_date ?? null)}
                            />
                        ) : row.effective_date_text ? (
                            row.effective_date_text
                        ) : (
                            ''
                        )}
                    </TableCell>
                )}

                {/* Expiry interval – only shown in edit mode */}
                {editRow !== -1 && (
                    <TableCell>{interval !== null ? interval : ''}</TableCell>
                )}

                {/* Status */}
                <TableCell>
                    {viewType === 'approval' && row.is_row_modified === true ? (
                        <Announcement className="text-[#0288d1]" />
                    ) : row.expired_date_status !== undefined ? (
                        row.expired_date_status === 'Valid' ||
                            row.expired_date_status === 'updated' ? (
                            <CheckCircle className="text-green-600" />
                        ) : row.expired_date_status === 'Expiring' ? (
                            <Info color="secondary" />
                        ) : (
                            <RemoveCircle color="error" />
                        )
                    ) : (
                        ''
                    )}
                </TableCell>

                {/* Edit actions */}
                <TableCell>
                    {editRow !== index && viewType === 'edit' ? (
                        <IconButton onClick={() => setEditMode(index, row)} color="default">
                            <Edit />
                        </IconButton>
                    ) : (
                        editRow === index &&
                        viewType === 'edit' && (
                            <div>
                                <IconButton
                                    onClick={() => saveEdit(row)}
                                    style={{ color: palette.success }}
                                >
                                    <Check />
                                </IconButton>
                                <IconButton
                                    onClick={() => cancelEdit()}
                                    style={{ color: palette.warning }}
                                >
                                    <Close />
                                </IconButton>
                                <IconButton
                                    onClick={() => deleteRow(row)}
                                    style={{ color: palette.error }}
                                >
                                    <Delete />
                                </IconButton>
                                {emptyValueValidation && (
                                    <Typography
                                        text={emptyValueValidation}
                                        color="error"
                                        fontWeight="bold"
                                    />
                                )}
                            </div>
                        )
                    )}
                </TableCell>
            </TableRow>
        );
    };


    const setEditMode = (index: number, row: CertificateRights) => {
        if (editRow === -1) {
            setEditRow(index);
            setTempRating(row.rating_name);
            setTempAircraftCategory(row.aircraft_category_name);
            const expDateToString = row.expiration_date.toString();
            const expDateToDate = DateTime.fromISO(expDateToString).toJSDate();

            const effDateToString = row.effective_date ? row.effective_date.toString() : '';
            const effDateToDate = effDateToString ? DateTime.fromISO(effDateToString).toJSDate() : null;

            //Because backend returns row.expiration_date as string. Here we need to parse that string to date so that
            //selectedDate can have the same date as row.expiration_date before user starts editing the dates
            if (typeof row.expiration_date === "string") {
                setSelectedDate(expDateToDate);
            }
            else {
                setSelectedDate(row.expiration_date);
            }
            if (typeof row.effective_date === "string") {
                setSelectedEffectiveDate(effDateToDate);
            }
        }
    }

    const onDateChange = (date: Date) => {
        const ratingName = tempRating;
        const categoryName = tempAircraftCategory;

        const interval = ratingName && categoryName
            ? findExpInterval(ratingName, categoryName)
            : null;

        if (interval !== null) {
            const newExpDate = DateTime.fromJSDate(date)
                .plus({ months: interval })
                .toJSDate();
            setSelectedEffectiveDate(date);
            setSelectedDate(newExpDate);
        } else {
            cancelEdit();
            setSnackbarMessage(
                "Cannot set expiration date. Please make sure there is an exp interval for the mxi license"
            );
            setSnackbarSeverity("error");
        }
    };

    const addRow = () => {
        if (editRow === -1) {
            setTempAircraftCategory('');
            setTempRating('');
            setSelectedDate(null);
            setSelectedEffectiveDate(null);

            const newRow: CertificateRights = {
                rating_name: '',
                aircraft_category_name: '',
                expiration_date: new Date(),
                expiration_date_text: '',
                effective_date: undefined,
                effective_date_text: '',
            };

            // ✅ Calculate the new index from the previous state
            setSelectedCertificate((prev: CertificateData) => {
                const updatedRights = [...prev.certificate_rights, newRow];
                const newIndex = updatedRights.length;

                // ✅ Set edit row AFTER we know the new length
                setEditRow(newIndex);

                return {
                    ...prev,
                    certificate_rights: updatedRights
                };
            });
        }
    };


    const setTempValueForRow = (value: string, type: 'ratings' | 'aircraft') => {
        const newRating = type === 'ratings' ? value : tempRating;
        const newCategory = type === 'aircraft' ? value : tempAircraftCategory;

        const interval =
            newRating && newCategory
                ? findExpInterval(newRating, newCategory)
                : null;

        if (interval === null) {
            setSelectedDate(null);
            setSelectedEffectiveDate(null);
            setEmptyValueValidation(
                'No exp interval found for this rating and aircraft/category combination'
            );
            if (type === 'ratings') setTempRating(value);
            if (type === 'aircraft') setTempAircraftCategory(value);
            return;
        } else {
            setEmptyValueValidation('');
        }

        if (type === 'ratings') {
            setTempRating(value);
        }
        if (type === 'aircraft') {
            setTempAircraftCategory(value);
        }
    };

    const saveEdit = (row: CertificateRights) => {
        const rowIndex = findRowIndex(row);
        if (rowIndex === -1) {
            return;
        }

        let selectedRow = selectedCertificate.certificate_rights[rowIndex];

        if (tempRating === '' || tempAircraftCategory === '' || selectedDate === null) {
            setEmptyValueValidation('Missing values. Please add them or cancel changes');
        }
        else {

            if (selectedRow.rating_name === '') {
                setChangesMade([...changesMade, `NEW ROW: ${tempRating}, ${tempAircraftCategory}, ${DateTime.fromJSDate(new Date(selectedDate)).toFormat('dd.LL.yyyy')}`]);
            }
            else {
                setChangesMade([...changesMade, `EDIT ROW: ${selectedRow.rating_name}, ${selectedRow.aircraft_category_name}, ${DateTime.fromJSDate(new Date(selectedRow.expiration_date)).toFormat('dd.LL.yyyy')} ==> ${tempRating}, ${tempAircraftCategory}, ${DateTime.fromJSDate(selectedDate).toFormat('dd.LL.yyyy')}`]);
            }
            if (tempRating !== '') {
                selectedRow.rating_name = tempRating;
                selectedRow.certificate_rating_id = ratings?.find(r => r.rating === tempRating)?.id;
            }
            if (tempAircraftCategory !== '') {
                selectedRow.aircraft_category_name = tempAircraftCategory;
                selectedRow.certificate_aircraft_category_id = categories?.find(a => a.aircraft_category === tempAircraftCategory)?.id
            }
            if (selectedDate !== undefined) {
                selectedRow.expiration_date = selectedDate;
                selectedRow.expiration_date_text = DateTime.fromJSDate(selectedDate).toFormat('dd.LL.yyyy');
            }
            if (selectedEffectiveDate !== null && selectedEffectiveDate !== undefined) {
                selectedRow.effective_date = selectedEffectiveDate;
                selectedRow.effective_date_text = DateTime.fromJSDate(selectedEffectiveDate).toFormat('dd.LL.yyyy');
            }
            setSelectedCertificate({ ...selectedCertificate });
            setEditRow(-1);
            setEmptyValueValidation('');
            setTempAircraftCategory('');
            setTempRating('');
            setSelectedDate(null);
            setSelectedEffectiveDate(null);
        }
    }

    const cancelEdit = () => {
        setEditRow(-1);
        setTempAircraftCategory('');
        setEmptyValueValidation('');
        setTempRating('');
        setSelectedDate(null);
        setSelectedEffectiveDate(null);
    }

    const deleteRow = (row: CertificateRights) => {
        const rowIndex = findRowIndex(row);
        if (rowIndex === -1) {
            return;
        }

        setChangesMade([
            ...changesMade,
            "DELETED ROW: " + row.rating_name + ', ' + row.aircraft_category_name + ', ' + row.expiration_date_text
        ]);

          setSelectedCertificate((prev: CertificateData) => ({
            ...prev,
            certificate_rights: prev.certificate_rights.filter((_, idx) => idx !== rowIndex)
        }));

        setEditRow(-1);
    }

    const submitCertificate = () => {
        if (jiraLink.length !== 0) {
            const isDuplicate = selectedCertificate.certificate_rights.some((item, index) => {
                return selectedCertificate.certificate_rights.findIndex((otherItem) =>
                    otherItem.rating_name === item.rating_name &&
                    otherItem.aircraft_category_name === item.aircraft_category_name
                ) !== index;
            });

            if (isDuplicate) { // Check for duplicate rows of the certificate rights
                setSnackbarMessage("Cannot submit certificate. There are duplicate rows in the certificate rights.");
                setSnackbarSeverity("error");
                setConfirmDialog(false);
                return;
            }
            let certificateToSubmit: CertificateData = {
                certificate_rights: selectedCertificate.certificate_rights,
                mechanic_employee_id: selectedCertificate.mechanic_employee_id,
                owner_employee_id: userId,
                modified_changes_in_text: changesMade,
                jira_link: jiraLink
            }
            setConfirmDialog(false);
            setLoading(true);
            CertificatesApi.CreateNewCertificateForEmployee(certificateToSubmit).then(res => {
                setLoading(false);
            })
        }
    }

    return (
        <div className="ml-6 mt-6">
            <CertificateInfo viewType={viewType} selectedCertificate={selectedCertificate} infoType="top" closeCertificate={() => closeCertificate()} shouldBeAbleToClose={shouldBeAbleToClose} />
            {(selectedCertificate.modified_changes_in_text !== undefined && selectedCertificate.modified_changes_in_text !== null && viewType === "approval") &&
                <div className="w-full lg:w-2/3 bg-[#0288d1] p-4">
                    {selectedCertificate.modified_changes_in_text!.map(change => (
                        <Typography variant="body1" fontWeight="bold" align="center" text={change} />
                    ))}
                </div>
            }
            <div className="flex flex-wrap w-full">
                <div className="w-full lg:w-5/6">
                    {
                        selectedCertificate.certificate_rights.length > 0 ?
                            <Table
                                sortTableColumns={editRow !== -1 ? sortTableColumnsInEdit : sortTableColumns}
                                rows={selectedCertificate.certificate_rights}
                                generateBody={(row: any, index: number) => generateTableBody(row, index)}
                                pagination={false}
                                skipIndexCol={true}
                            />
                            :
                            <Typography text={"You have no authorization"} variant='h1' />
                    }
                </div>
            </div>
            <div className="flex flex-wrap w-full">
                {viewType === "edit" &&
                    <div>
                        <div className="w-full">
                            <IconButton onClick={() => addRow()} style={{ color: palette.success }} >
                                <AddCircle />
                            </IconButton>
                        </div>
                        <div className="w-full">
                            <Button disabled={editRow !== -1 || changesMade.length === 0} variant="contained" color="primary" onClick={() => setConfirmDialog(true)}>Submit Certificate for Approval</Button>
                        </div>
                    </div>
                }
                <Dialog
                    visible={confirmDialog}
                    max_width={"md"} title={"Confirm these changes for approval"}
                    context={
                        <div>
                            {
                                changesMade.map(row =>
                                    <div className="flex justify-center w-full m-2 p-2">
                                        <Typography variant="body1" align="center" text={row} color="primary" />
                                    </div>
                                )}
                            <div className="flex flex-wrap justify-center">
                                <Input width={60} multiline={false} help_text={""} label_text={"Please provide jira link"} value={jiraLink} onTextChange={(text: string) => setJiraLink(text)} type="text" />
                            </div>
                            <div className="flex justify-center m-2 p-2">
                                <div className="p-2">
                                    <Button variant="contained" color="primary" onClick={() => submitCertificate()}>Confirm Certificate for approval</Button>
                                </div>
                            </div>
                        </div>
                    }
                    onClose={(status: any) => setConfirmDialog(false)}
                    strict={false}
                    show_options={false}
                />
            </div>
            <CertificateInfo viewType={viewType} selectedCertificate={selectedCertificate} infoType="bottom" closeCertificate={() => { }} shouldBeAbleToClose={shouldBeAbleToClose} />
            {viewType === 'approval' && selectedCertificate.certificate_id !== undefined &&
                <ProcessApproval
                    ratingsDifference={ratingsDifference!}
                    approvalSendBy={selectedCertificate.approval_sent_by_full_name!}
                    changes={selectedCertificate.modified_changes_in_text!}
                    certificates={selectedCertificate.certificate_rights}
                    employeeId={selectedCertificate.mechanic_employee_id}
                    closeCertificate={() => closeCertificate()}
                    certificateId={selectedCertificate.certificate_id}
                    approverId={userId}
                />
            }
            <Snackbar
                onSnackBarClose={() => setSnackbarMessage("")}
                open={snackbarMessage.length > 0}
                message={snackbarMessage}
                autoHide={false}
                horizontalPosition='right'
                severity={snackbarSeverity}
            >
            </Snackbar>
        </div>
    );
};
export default Certificate;